import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  ActiveQuestion,
  AgoraToken,
  CreateStreamSessionResult,
  LeaderboardEntry,
  OpenQuestionResult,
  StreamPrizeTier,
  StreamQuestion,
  StreamQuestionResults,
  StreamSession,
} from '@domain/models/streaming.model';
import { StreamingRepository } from '@domain/repositories/streaming.repository';

import type { ApiPagedResponse } from '@data/dto/api.dto';
import type {
  ActiveQuestionDto,
  AgoraTokenDto,
  CreateStreamSessionDto,
  LeaderboardEntryDto,
  OpenQuestionDto,
  StreamPrizeTierDto,
  StreamQuestionDto,
  StreamQuestionResultsDto,
  StreamSessionDto,
} from '@data/dto/streaming.dto';
import {
  mapActiveQuestion,
  mapAgoraToken,
  mapCreateStreamSession,
  mapOpenQuestion,
  mapPagedLeaderboard,
  mapPrizeTier,
  mapStreamQuestion,
  mapStreamQuestionResults,
  mapStreamSession,
} from '@data/mappers/streaming.mapper';

@Injectable()
export class StreamingApiRepository extends StreamingRepository {
  private readonly http = inject(HttpClient);
  private readonly path = 'api/Streaming';

  listSessions(): Observable<readonly StreamSession[]> {
    return this.http
      .get<StreamSessionDto[]>(`${this.path}/sessions`)
      .pipe(map((rows) => rows.map(mapStreamSession)));
  }

  createSession(title: string): Observable<CreateStreamSessionResult> {
    return this.http
      .post<CreateStreamSessionDto>(`${this.path}/sessions`, { title })
      .pipe(map(mapCreateStreamSession));
  }

  startSession(streamSessionId: number): Observable<void> {
    return this.http.post<void>(`${this.path}/sessions/${streamSessionId}/start`, {});
  }

  endSession(streamSessionId: number): Observable<void> {
    return this.http.post<void>(`${this.path}/sessions/${streamSessionId}/end`, {});
  }

  getAgoraToken(streamSessionId: number): Observable<AgoraToken> {
    return this.http
      .post<AgoraTokenDto>(`${this.path}/sessions/${streamSessionId}/Agora-token`, {})
      .pipe(map(mapAgoraToken));
  }

  listStreamQuestions(streamSessionId: number): Observable<readonly StreamQuestion[]> {
    return this.http
      .get<StreamQuestionDto[]>(`${this.path}/sessions/${streamSessionId}/questions`)
      .pipe(map((rows) => rows.map(mapStreamQuestion)));
  }

  attachQuestion(
    streamSessionId: number,
    questionId: number,
  ): Observable<{ streamQuestionId: number }> {
    return this.http.post<{ streamQuestionId: number }>(
      `${this.path}/sessions/${streamSessionId}/questions`,
      { questionId },
    );
  }

  openQuestion(
    streamSessionId: number,
    streamQuestionId: number,
  ): Observable<OpenQuestionResult> {
    return this.http
      .post<OpenQuestionDto>(
        `${this.path}/sessions/${streamSessionId}/questions/${streamQuestionId}/open`,
        {},
      )
      .pipe(map(mapOpenQuestion));
  }

  closeQuestion(streamSessionId: number, streamQuestionId: number): Observable<void> {
    return this.http.post<void>(
      `${this.path}/sessions/${streamSessionId}/questions/${streamQuestionId}/close`,
      {},
    );
  }

  getActiveQuestion(streamSessionId: number): Observable<ActiveQuestion | null> {
    return this.http
      .get<ActiveQuestionDto | null>(`${this.path}/sessions/${streamSessionId}/active-question`)
      .pipe(map(mapActiveQuestion));
  }

  getQuestionResults(
    streamSessionId: number,
    streamQuestionId: number,
  ): Observable<StreamQuestionResults> {
    return this.http
      .get<StreamQuestionResultsDto>(
        `${this.path}/sessions/${streamSessionId}/questions/${streamQuestionId}/results`,
      )
      .pipe(map(mapStreamQuestionResults));
  }

  getLeaderboard(
    streamSessionId: number,
    query: PageQuery = { page: 1, pageSize: 50 },
  ): Observable<PagedResult<LeaderboardEntry>> {
    const params = new HttpParams({
      fromObject: {
        page: String(query.page),
        limit: String(query.pageSize),
      },
    });
    return this.http
      .get<ApiPagedResponse<LeaderboardEntryDto>>(
        `${this.path}/sessions/${streamSessionId}/leaderboard`,
        { params },
      )
      .pipe(map(mapPagedLeaderboard));
  }

  getPrizeTiers(streamSessionId: number): Observable<readonly StreamPrizeTier[]> {
    return this.http
      .get<StreamPrizeTierDto[]>(`${this.path}/sessions/${streamSessionId}/prize-tiers`)
      .pipe(map((rows) => rows.map(mapPrizeTier)));
  }

  setPrizeTiers(
    streamSessionId: number,
    tiers: readonly StreamPrizeTier[],
  ): Observable<void> {
    return this.http.put<void>(`${this.path}/sessions/${streamSessionId}/prize-tiers`, { tiers });
  }
}
